import type { ReactNode } from 'react';
import { Icon } from '../shell/Icon.tsx';
import { Empty } from './Empty.tsx';
import styles from './SurfaceShell.module.css';

/* ==========================================================================
   SurfaceShell — the page frame.

   Identity block (breadcrumb → 20px title → inline live state) over a
   hairline, then the rest of the viewport is data. Exactly one primary verb
   belongs in `actions`, top-right.
   ========================================================================== */

interface SurfaceShellProps {
  /** Breadcrumb or back link. One line, sentence case, above the title —
      the same crumb the shell's workspace strip prints on the routes that
      have one. */
  eyebrow?: ReactNode;
  title: string;
  /** Live state belonging to the object: dots, meters, one chip. Not chrome. */
  subtitle?: ReactNode;
  /** Controls. One primary, the rest secondary, destructive last. */
  actions?: ReactNode;
  /** A tab strip sits flush under the identity block, above the body. */
  tabs?: ReactNode;
  pad?: 'seam' | 'none' | undefined;
  children: ReactNode;
}

export function SurfaceShell({
  eyebrow,
  title,
  subtitle,
  actions,
  tabs,
  pad = 'seam',
  children,
}: SurfaceShellProps) {
  return (
    <section className={styles.surface}>
      <header className={styles.head}>
        <div className={styles.idBlock}>
          {eyebrow !== undefined && <div className={styles.crumb}>{eyebrow}</div>}
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle !== undefined && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
        </div>
        {actions !== undefined && <div className={styles.actions}>{actions}</div>}
      </header>
      {tabs}
      <div className={styles.body} data-pad={pad === 'none' ? 'none' : undefined}>
        {children}
      </div>
    </section>
  );
}

/** `← Fleet` — the back link that lives in the breadcrumb slot. */
export function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={styles.back} onClick={onClick}>
      <Icon name="arrow-left" size={12} />
      {label}
    </button>
  );
}

/**
 * Retained for surfaces that have nothing to render yet. One line of text —
 * no dashed box, no illustration, no centred call to action.
 */
export function Placeholder({ label, hint }: { label: string; hint?: string | undefined }) {
  return <Empty prompt detail={hint}>{label}</Empty>;
}
